import React, { useEffect, useState, useRef } from "react";
import { Link, NavLink, useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import { Variables } from "../../Variations/Variations";
import { toast } from "react-toastify";
import { AiOutlineRight } from "react-icons/ai";

const ManageSlide = () => {
  const { SlideID } = useParams();
  const navigate = useNavigate();
  const slideIdNum = Number(SlideID) || 0;

  // ---- State (matches NEW API JSON) ----
  const [name, setName] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [redirectUrl, setRedirectUrl] = useState("");
  const [isPublished, setIsPublished] = useState(false);
  const [buttonText, setButtonText] = useState("");

  const [textLine1, setTextLine1] = useState("");
  const [textLine2, setTextLine2] = useState("");
  const [textLine1Ar, setTextLine1Ar] = useState("");
  const [textLine2Ar, setTextLine2Ar] = useState("");
  const [buttonTextAr, setButtonTextAr] = useState("");

  const [widgetId, setWidgetId] = useState(null); // number|null
  const [styleId, setStyleId] = useState(null); // 1, 2, or 3

  // ---- Widgets for select ----
  const [widgets, setWidgets] = useState([]);
  const widgetOptions = (Array.isArray(widgets) ? widgets : [])
    .filter((w) => parseInt(w.WidgetTypeId) === 2)
    .map((w) => ({
      value: Number(w.Id),
      label: w.DisplayName ?? w.Name ?? String(w.Id),
    }));
  const selectedWidgetOption =
    widgetOptions.find((o) => o.value === Number(widgetId)) || null;

  // ---- Styles select ----
  const styleOptions = [
    { value: 1, label: "Style 1" },
    { value: 2, label: "Style 2" },
    { value: 3, label: "Style 3" },
  ];
  const selectedStyleOption =
    styleOptions.find((o) => o.value === Number(styleId)) || null;

  // ---- Refs / utils ----
  const fileInputRef = useRef(null);
  const isBlank = (v) => typeof v !== "string" || v.trim() === "";

  // ---- Derived UI visibility from styleId ----
  const styleNum = Number(styleId) || null;
  const hideAllTextAndBtnForStyle2 = styleNum === 2;
  const hideOnlyTextLine2ForStyle3 = styleNum === 3;

  // Convenience flags
  const showTextLine1Blocks = !hideAllTextAndBtnForStyle2; // shown for styles 1 & 3
  const showTextLine2Blocks = !hideAllTextAndBtnForStyle2 && !hideOnlyTextLine2ForStyle3; // shown only for style 1
  const showButtonBlocks = !hideAllTextAndBtnForStyle2; // shown for styles 1 & 3
  const showRedirectUrl = !hideAllTextAndBtnForStyle2; // shown for styles 1 & 3

  // ---- Loaders ----
  async function loadWidgets() {
    try {
      const res = await fetch(`${Variables.API_URL}Rasha/Widget/Get`);
      if (!res.ok) throw new Error();
      setWidgets(await res.json());
    } catch {
      toast.error("Failed to load widgets");
    }
  }

  async function loadSlide() {
    try {
      const res = await fetch(`${Variables.API_URL}Rasha/Slide/Get/${slideIdNum}`);
      if (!res.ok) return;
      const data = await res.json();
      const s = data?.Slide ?? data ?? {};

      setName(s.name ?? s.Name ?? "");
      setImageUrl(s.imageUrl ?? s.ImageUrl ?? "");
      setRedirectUrl(s.redirectUrl ?? s.RedirectUrl ?? "");
      setIsPublished(
        typeof s.isPublished === "boolean" ? s.isPublished : Boolean(s.IsPublished)
      );
      setButtonText(s.buttonText ?? s.ButtonText ?? "");

      setTextLine1(s.textLine1 ?? s.TextLine1 ?? "");
      setTextLine2(s.textLine2 ?? s.TextLine2 ?? "");
      setTextLine1Ar(s.textLine1Ar ?? s.TextLine1Ar ?? "");
      setTextLine2Ar(s.textLine2Ar ?? s.TextLine2Ar ?? "");
      setButtonTextAr(s.buttonTextAr ?? s.ButtonTextAr ?? "");

      setWidgetId(
        s.widgetId != null ? Number(s.widgetId) : s.WidgetId != null ? Number(s.WidgetId) : null
      );
      setStyleId(
        s.styleId != null ? Number(s.styleId) : s.StyleId != null ? Number(s.StyleId) : null
      );
    } catch {
      toast.error("Failed to load slide");
    }
  }

  useEffect(() => {
    loadWidgets();
    if (slideIdNum !== 0) loadSlide();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slideIdNum]);

  // ---- Normal <input type="file"> upload (<= 2MB) ----
  const onFileInputChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size >= 2_000_000) {
      toast.error("Image is not accepted (max 2MB).");
      e.target.value = "";
      return;
    }

    const formdata = new FormData();
    formdata.append("Images", file);

    try {
      const response = await fetch(
        `${Variables.API_URL}UploadMedia/UploadImageToServer`,
        { method: "POST", body: formdata, redirect: "follow" }
      );
      const result = await response.json();
      setImageUrl(result?.[0] ?? "");
    } catch (error) {
      console.error(error);
      toast.error("Image upload failed");
    }
  };

  // ---- Validation ----
  function isValid() {
    if (isBlank(name)) {
      toast.error("Name is required");
      return false;
    }
    if (isBlank(imageUrl)) {
      toast.error("Image is required (upload an image)");
      return false;
    }
    if (!widgetId || Number(widgetId) <= 0) {
      toast.error("Widget is required");
      return false;
    }
    if (!styleId || ![1, 2, 3].includes(Number(styleId))) {
      toast.error("Style is required (choose Style 1, Style 2, or Style 3)");
      return false;
    }
    return true;
  }

  // ---- Save (Add / Update) ----
  async function handleSubmit(e) {
    e.preventDefault();
    if (!isValid()) return;

    try {
      const payload = {
        id: slideIdNum || 0,
        name,
        imageUrl,
        redirectUrl,
        isPublished,
        buttonText,
        textLine1,
        textLine2,
        widgetId: Number(widgetId),
        styleId: Number(styleId),
        textLine1Ar,
        textLine2Ar,
        buttonTextAr,
      };

      const res = await fetch(`${Variables.API_URL}Rasha/Slide/Upsert`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error(await res.text());

      toast.success(slideIdNum === 0 ? "Slide added successfully" : "Slide updated successfully");
      navigate("/admin/rasha/Slides");
    } catch (err) {
      toast.error(err.message || "Failed to save");
    }
  }

  // ---- Render ----
  return (
    <div className="rightSide">
      <div className="container-fluid mt-3">
        {/* Header */}
        <div className="row mb-3">
          <div className="col-12">
            <h2>{slideIdNum === 0 ? "Add" : `Manage ${name || "Slide"}`} Slide</h2>
            <ul className="portalPath">
              <li>
                Home <AiOutlineRight />
              </li>
              <li>
                <Link to="/admin/rasha/Slides">
                  Slides <AiOutlineRight />
                </Link>
              </li>
              <li>{slideIdNum === 0 ? "Add" : "Manage"} Slide</li>
            </ul>
          </div>
        </div>

        {/* Form */}
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                <div className="d-flex justify-content-end mb-3">
                  <NavLink to="/admin/rasha/Slides" className="btn btn-secondary me-2">
                    Cancel
                  </NavLink>
                  <button className="btn btn-primary" onClick={handleSubmit}>
                    Save
                  </button>
                </div>

                <div className="row gy-3">
                  {/* Name */}
                  <div className="col-lg-6">
                    <label className="mb-2">
                      Name <span className="required">*</span>
                    </label>
                    <input
                      id="name"
                      className="form-control"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Internal name / reference"
                    />
                  </div>

                  {/* Publish toggle */}
                  <div className="form-group col-lg-3 d-flex align-items-center">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="publish"
                      checked={isPublished}
                      onChange={(e) => setIsPublished(e.target.checked)}
                    />
                    &nbsp;
                    <label className="form-check-label" htmlFor="publish">
                      Publish
                    </label>
                  </div>

                  {/* Widget select */}
                  <div className="col-lg-6">
                    <label className="mb-2">
                      Widget <span className="required">*</span>
                    </label>
                    <Select
                      options={widgetOptions}
                      value={selectedWidgetOption}
                      onChange={(opt) => setWidgetId(opt ? opt.value : null)}
                      placeholder="Select a widget…"
                      isClearable
                    />
                  </div>

                  {/* Style select */}
                  <div className="col-lg-6">
                    <label className="mb-2">
                      Style <span className="required">*</span>
                    </label>
                    <Select
                      options={styleOptions}
                      value={selectedStyleOption}
                      onChange={(opt) => setStyleId(opt ? opt.value : null)}
                      placeholder="Select a style…"
                      isClearable
                    />
                  </div>

                  {/* Text lines (EN) */}
                  {showTextLine1Blocks && (
                    <div className="col-lg-6">
                      <label className="mb-2">Text Line 1</label>
                      <input
                        id="textLine1"
                        className="form-control"
                        value={textLine1}
                        onChange={(e) => setTextLine1(e.target.value)}
                        placeholder="Heading line"
                      />
                    </div>
                  )}
                  {showTextLine2Blocks && (
                    <div className="col-lg-6">
                      <label className="mb-2">Text Line 2</label>
                      <input
                        id="textLine2"
                        className="form-control"
                        value={textLine2}
                        onChange={(e) => setTextLine2(e.target.value)}
                        placeholder="Subheading line"
                      />
                    </div>
                  )}

                  {/* Text lines (AR) */}
                  {showTextLine1Blocks && (
                    <div className="col-lg-6">
                      <label className="mb-2">Text Line 1 (Arabic)</label>
                      <input
                        id="textLine1Ar"
                        className="form-control"
                        value={textLine1Ar}
                        onChange={(e) => setTextLine1Ar(e.target.value)}
                        placeholder="النص الأول"
                      />
                    </div>
                  )}
                  {showTextLine2Blocks && (
                    <div className="col-lg-6">
                      <label className="mb-2">Text Line 2 (Arabic)</label>
                      <input
                        id="textLine2Ar"
                        className="form-control"
                        value={textLine2Ar}
                        onChange={(e) => setTextLine2Ar(e.target.value)}
                        placeholder="النص الثاني"
                      />
                    </div>
                  )}

                  {/* Button text (EN/AR) */}
                  {showButtonBlocks && (
                    <div className="col-lg-6">
                      <label className="mb-2">Button Text</label>
                      <input
                        id="buttonText"
                        className="form-control"
                        value={buttonText}
                        onChange={(e) => setButtonText(e.target.value)}
                        placeholder="e.g., Learn more"
                      />
                    </div>
                  )}
                  {showButtonBlocks && (
                    <div className="col-lg-6">
                      <label className="mb-2">Button Text (Arabic)</label>
                      <input
                        id="buttonTextAr"
                        className="form-control"
                        value={buttonTextAr}
                        onChange={(e) => setButtonTextAr(e.target.value)}
                        placeholder="نص الزر"
                      />
                    </div>
                  )}

                  {/* Redirect URL */}
                  {showRedirectUrl && (
                    <div className="col-lg-12">
                      <label className="mb-2">Redirect URL</label>
                      <input
                        id="redirectUrl"
                        className="form-control"
                        value={redirectUrl}
                        onChange={(e) => setRedirectUrl(e.target.value)}
                        placeholder="redirect Url"
                      />
                    </div>
                  )}

                  {/* Image uploader */}
                  <div className="col-lg-6">
                    <label className="mb-2">Image (max 2MB)</label>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      className="form-control"
                      onChange={onFileInputChange}
                    />
                    {imageUrl && (
                      <div className="mt-3">
                        <img
                          src={Variables.API_PUBLISHED + imageUrl}
                          alt="preview"
                          style={{ maxWidth: "100%", maxHeight: 200, display: "block" }}
                        />
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger mt-2"
                          onClick={() => setImageUrl("")}
                        >
                          Remove Image
                        </button>
                        <small className="text-muted d-block mt-2">
                          Uploaded URL: {imageUrl}
                        </small>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div> 
    </div>
  );
};

export default ManageSlide;
